/**
 * Country calling codes (DDI) for contact creation.
 *
 * Americas are complete for North / Central / South (+ main Caribbean).
 * Previous European options are kept under `other` so existing flows do not lose options.
 *
 * `code` is digits only (no "+"), matching WhatsApp JID storage.
 */

export type CountryCallingRegion =
  | 'south'
  | 'central'
  | 'north'
  | 'caribbean'
  | 'other'

export type CountryCallingCode = {
  code: string
  name: string
  flag: string
  region: CountryCallingRegion
}

/** Default for new contacts — keeps BR-first UX. */
export const DEFAULT_COUNTRY_CALLING_CODE = '55'

/**
 * Ordered for the select: Brazil first, then Americas by region, then other (EU).
 * NANP shares +1 (USA / Canada / several Caribbean territories).
 */
export const COUNTRY_CALLING_CODES: readonly CountryCallingCode[] = [
  // South America
  { code: '55', name: 'Brasil', flag: '🇧🇷', region: 'south' },
  { code: '54', name: 'Argentina', flag: '🇦🇷', region: 'south' },
  { code: '591', name: 'Bolívia', flag: '🇧🇴', region: 'south' },
  { code: '56', name: 'Chile', flag: '🇨🇱', region: 'south' },
  { code: '57', name: 'Colômbia', flag: '🇨🇴', region: 'south' },
  { code: '593', name: 'Equador', flag: '🇪🇨', region: 'south' },
  { code: '594', name: 'Guiana Francesa', flag: '🇬🇫', region: 'south' },
  { code: '592', name: 'Guiana', flag: '🇬🇾', region: 'south' },
  { code: '595', name: 'Paraguai', flag: '🇵🇾', region: 'south' },
  { code: '51', name: 'Peru', flag: '🇵🇪', region: 'south' },
  { code: '597', name: 'Suriname', flag: '🇸🇷', region: 'south' },
  { code: '598', name: 'Uruguai', flag: '🇺🇾', region: 'south' },
  { code: '58', name: 'Venezuela', flag: '🇻🇪', region: 'south' },

  // Central America
  { code: '501', name: 'Belize', flag: '🇧🇿', region: 'central' },
  { code: '506', name: 'Costa Rica', flag: '🇨🇷', region: 'central' },
  { code: '503', name: 'El Salvador', flag: '🇸🇻', region: 'central' },
  { code: '502', name: 'Guatemala', flag: '🇬🇹', region: 'central' },
  { code: '504', name: 'Honduras', flag: '🇭🇳', region: 'central' },
  { code: '505', name: 'Nicarágua', flag: '🇳🇮', region: 'central' },
  { code: '507', name: 'Panamá', flag: '🇵🇦', region: 'central' },

  // North America (+ NANP Caribbean: Porto Rico, RD, Jamaica, etc. use +1 with local area code)
  { code: '1', name: 'EUA / Canadá / Caribe (+1)', flag: '🇺🇸', region: 'north' },
  { code: '52', name: 'México', flag: '🇲🇽', region: 'north' },

  // Caribbean with own ITU country codes (not NANP area codes)
  { code: '297', name: 'Aruba', flag: '🇦🇼', region: 'caribbean' },
  { code: '53', name: 'Cuba', flag: '🇨🇺', region: 'caribbean' },
  { code: '599', name: 'Curaçao / Caribe NL', flag: '🇨🇼', region: 'caribbean' },
  { code: '590', name: 'Guadalupe', flag: '🇬🇵', region: 'caribbean' },
  { code: '509', name: 'Haiti', flag: '🇭🇹', region: 'caribbean' },
  { code: '596', name: 'Martinica', flag: '🇲🇶', region: 'caribbean' },

  // Kept from previous picker (non-Americas) — avoid removing options users already rely on
  { code: '351', name: 'Portugal', flag: '🇵🇹', region: 'other' },
  { code: '34', name: 'Espanha', flag: '🇪🇸', region: 'other' },
  { code: '44', name: 'Reino Unido', flag: '🇬🇧', region: 'other' },
  { code: '33', name: 'França', flag: '🇫🇷', region: 'other' },
  { code: '49', name: 'Alemanha', flag: '🇩🇪', region: 'other' },
  { code: '39', name: 'Itália', flag: '🇮🇹', region: 'other' },
] as const

export function getCountryCallingCodeLabel(country: CountryCallingCode): string {
  return `+${country.code} (${country.name})`
}

export function getCountryCallingCodeMobileLabel(country: CountryCallingCode): string {
  return `${country.flag} +${country.code}`
}

export function digitsOnlyPhone(value: string): string {
  return (value || '').replace(/\D/g, '')
}

/** Compose DDI + national digits. If national already includes the DDI, keep as-is. */
export function joinPhoneWithCountryCode(countryCode: string, national: string): string {
  const cc = digitsOnlyPhone(countryCode)
  const nat = digitsOnlyPhone(national)
  if (!nat) return ''
  if (nat.startsWith(cc)) return nat
  return `${cc}${nat}`
}

/**
 * Split a stored phone into DDI + national number for editors.
 * Preserves legacy BR team phones (10–11 digits without DDI).
 */
export function splitPhoneWithCountryCode(fullPhone: string | null | undefined): {
  countryCode: string
  national: string
} {
  const digits = digitsOnlyPhone(fullPhone || '')
  if (!digits) {
    return { countryCode: DEFAULT_COUNTRY_CALLING_CODE, national: '' }
  }

  if (digits.startsWith('55') && digits.length >= 12) {
    return { countryCode: '55', national: digits.slice(2) }
  }

  if (digits.length >= 12) {
    const codes = [...new Set(COUNTRY_CALLING_CODES.map((c) => c.code))].sort(
      (a, b) => b.length - a.length
    )
    for (const code of codes) {
      if (code === '55') continue
      if (digits.startsWith(code) && digits.length - code.length >= 8) {
        return { countryCode: code, national: digits.slice(code.length) }
      }
    }
    return { countryCode: DEFAULT_COUNTRY_CALLING_CODE, national: digits }
  }

  // NANP with country code: 1 + 10 digits (area code starts with 2–9)
  if (
    digits.length === 11 &&
    digits.startsWith('1') &&
    digits[1] >= '2' &&
    digits[1] <= '9'
  ) {
    return { countryCode: '1', national: digits.slice(1) }
  }

  // Legacy BR (how TeamTab stored phones before the DDI selector)
  if (digits.length === 10 || digits.length === 11) {
    return { countryCode: '55', national: digits }
  }

  return { countryCode: DEFAULT_COUNTRY_CALLING_CODE, national: digits }
}

/** Human-readable phone for lists (BR keeps familiar mask). */
export function formatStoredPhoneForDisplay(fullPhone: string | null | undefined): string {
  const digits = digitsOnlyPhone(fullPhone || '')
  if (!digits) return ''

  const { countryCode, national } = splitPhoneWithCountryCode(digits)

  if (countryCode === '55' && (national.length === 10 || national.length === 11)) {
    if (national.length === 11) {
      return `+55 (${national.slice(0, 2)}) ${national.slice(2, 7)}-${national.slice(7)}`
    }
    return `+55 (${national.slice(0, 2)}) ${national.slice(2, 6)}-${national.slice(6)}`
  }

  return `+${digits}`
}
